/**
 * Application Constants
 *
 * Centralized configuration values to avoid magic numbers/strings throughout the codebase.
 * Following Stripe/Mollie patterns for maintainability and clarity.
 */

// =============================================================================
// FEATURE FLAGS
// =============================================================================

/**
 * Media-laag fase 1 (CompanyCam-exit): eigen in-app camera + R2-upload.
 * Flag uit = exact het oude CompanyCam-gedrag (rollback-pad zonder deploy
 * van datamodel of cron).
 */
export const MEDIA_EIGEN = import.meta.env.VITE_MEDIA_EIGEN === 'true';

/**
 * "Deels afgeleverd…" op de afleverstop (spiegel van "Deels opgehaald…").
 * Flag uit = knop weg; de chauffeur belt kantoor, dat het nalevering-verb op
 * de orderpagina gebruikt. Server-pad is richting-onafhankelijk
 * (`/v1/field/stops/{id}/partial`), dus rollback is alleen deze knop.
 */
export const DEELS_AFGELEVERD = import.meta.env.VITE_DEELS_AFGELEVERD === 'true';

// =============================================================================
// TIMING & POLLING
// =============================================================================

export const POLLING = {
  /** Payment status check interval in milliseconds */
  PAYMENT_STATUS_MS: 3000,
  /** Sync queue processing interval in milliseconds */
  SYNC_INTERVAL_MS: 30000,
  /** Route refresh interval in milliseconds */
  ROUTE_REFRESH_MS: 60000,
} as const;

export const TIMEOUTS = {
  /** Animation duration for card completion in milliseconds */
  CARD_COMPLETE_ANIMATION_MS: 300,
  /** Delay before scrolling to next card in milliseconds */
  SCROLL_DELAY_MS: 100,
  /** Haptic feedback duration in milliseconds */
  HAPTIC_FEEDBACK_MS: 50,
  /** Toast/notification display duration in milliseconds */
  TOAST_DURATION_MS: 3000,
} as const;

// =============================================================================
// SYNC & RETRY
// =============================================================================

export const SYNC = {
  /** Maximum number of retry attempts for failed sync operations */
  MAX_RETRIES: 3,
  /** Delay between retry attempts in milliseconds */
  RETRY_DELAY_MS: 5000,
  /** Number of items to process in a single batch */
  BATCH_SIZE: 5,
  /** IndexedDB database name */
  DB_NAME: 'kleda-field-app',
  /** IndexedDB database version */
  DB_VERSION: 1,
} as const;

// =============================================================================
// API & NETWORK
// =============================================================================

export const API = {
  /** Base path for field app API endpoints */
  FIELD_APP_PATH: '/v1/field',
  /** Request timeout in milliseconds */
  REQUEST_TIMEOUT_MS: 30000,
  /** Maximum file upload size in bytes (10MB) */
  MAX_UPLOAD_SIZE_BYTES: 10 * 1024 * 1024,
} as const;

// =============================================================================
// UI LIMITS
// =============================================================================

export const LIMITS = {
  /** Maximum length for skip reason text */
  SKIP_REASON_MAX_LENGTH: 500,
  /** Maximum length for notes/comments */
  NOTES_MAX_LENGTH: 1000,
  /** Maximum number of photos per item */
  MAX_PHOTOS_PER_ITEM: 10,
  /** Minimum dimension for rug in cm */
  RUG_MIN_DIMENSION_CM: 10,
  /** Maximum dimension for rug in cm */
  RUG_MAX_DIMENSION_CM: 2000,
} as const;

// =============================================================================
// PAYMENT
// =============================================================================

export const PAYMENT = {
  /** Supported payment methods */
  METHODS: ['pin', 'contant', 'ideal', 'bancontact'] as const,
  /** Currency code */
  CURRENCY: 'EUR',
  /** Minimum payment amount in euros */
  MIN_AMOUNT_EUR: 0.01,
  /** QR code size in pixels */
  QR_CODE_SIZE_PX: 200,
  /** QR code API base URL */
  QR_CODE_API_URL: 'https://quickchart.io/qr',
} as const;

export type PaymentMethod = typeof PAYMENT.METHODS[number];

// =============================================================================
// SKIP REASONS
// =============================================================================

export const SKIP_REASONS = [
  { value: 'Niet thuis', label: 'Niet thuis' },
  { value: 'Geen toegang', label: 'Geen toegang' },
  { value: 'Te zwaar / te groot', label: 'Te zwaar / te groot' },
  { value: 'Verkeerd adres', label: 'Verkeerd adres' },
  { value: 'Klant zegt af', label: 'Klant zegt af' },
  { value: 'Anders', label: 'Anders' },
] as const;

export type SkipReason = typeof SKIP_REASONS[number]['value'];

/**
 * Redenen bij "Deels afgeleverd". Vast, zodat kantoor meet wie er iets aan
 * moet doen: vergeten = chauffeur, lag niet in de bus = inpakken/klaarleggen,
 * niet aangenomen = klant. 'Anders' vraagt een toelichting.
 */
export const DEELS_AFGELEVERD_REDENEN = [
  { value: 'Vergeten in de bus', label: 'Vergeten in de bus' },
  { value: 'Lag niet in de bus', label: 'Lag niet in de bus' },
  { value: 'Klant nam het niet aan', label: 'Klant nam het niet aan' },
  { value: 'Anders', label: 'Anders' },
] as const;

export type DeelsAfgeleverdReden = typeof DEELS_AFGELEVERD_REDENEN[number]['value'];

// =============================================================================
// ATTEMPTED ACTIONS (for skip flow)
// =============================================================================

export const ATTEMPTED_ACTIONS = [
  { value: 'Gebeld', label: 'Gebeld' },
  { value: 'Aangebeld', label: 'Aangebeld' },
  { value: 'SMS/WhatsApp gestuurd', label: 'SMS/WhatsApp gestuurd' },
  { value: 'Bij buren gevraagd', label: 'Bij buren gevraagd' },
] as const;

export type AttemptedAction = typeof ATTEMPTED_ACTIONS[number]['value'];

// =============================================================================
// ROUTE & STOP TYPES
// =============================================================================

export const STOP_TYPES = {
  OPHALEN: 'Ophalen',
  TERUGBRENGEN: 'Terugbrengen',
} as const;

export type StopType = typeof STOP_TYPES[keyof typeof STOP_TYPES];

// =============================================================================
// STATUS VALUES
// =============================================================================

export const STOP_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
} as const;

export const PAYMENT_STATUS = {
  OPEN: 'open',
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
} as const;

// =============================================================================
// PRODUCT CATEGORIES
// =============================================================================

export const PRODUCT_CATEGORIES = {
  PRIMARY: 'Primary',
  EXTRA: 'Extra',
  PACKAGE: 'Package',
} as const;

export type ProductCategory = typeof PRODUCT_CATEGORIES[keyof typeof PRODUCT_CATEGORIES];

// =============================================================================
// PRESET DIMENSIONS (for AddItemPage)
// =============================================================================

export const PRESET_DIMENSIONS = [
  { length: 200, width: 140, label: '200 × 140 cm' },
  { length: 200, width: 200, label: '200 × 200 cm' },
  { length: 230, width: 160, label: '230 × 160 cm' },
  { length: 240, width: 170, label: '240 × 170 cm' },
  { length: 250, width: 200, label: '250 × 200 cm' },
  { length: 290, width: 200, label: '290 × 200 cm' },
  { length: 300, width: 200, label: '300 × 200 cm' },
  { length: 300, width: 250, label: '300 × 250 cm' },
  { length: 350, width: 250, label: '350 × 250 cm' },
  { length: 400, width: 300, label: '400 × 300 cm' },
] as const;

export const PRESET_DIAMETERS = [
  { diameter: 150, label: 'Ø 150 cm' },
  { diameter: 200, label: 'Ø 200 cm' },
  { diameter: 250, label: 'Ø 250 cm' },
  { diameter: 300, label: 'Ø 300 cm' },
  { diameter: 350, label: 'Ø 350 cm' },
  { diameter: 400, label: 'Ø 400 cm' },
] as const;

// =============================================================================
// ERROR MESSAGES (Dutch)
// =============================================================================

export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Geen internetverbinding. Controleer je netwerk en probeer opnieuw.',
  TIMEOUT_ERROR: 'De server reageert niet. Probeer het later opnieuw.',

  // Auth errors
  SESSION_EXPIRED: 'Je sessie is verlopen. Log opnieuw in.',
  UNAUTHORIZED: 'Je hebt geen toegang tot deze functie.',

  // Data errors
  LOAD_FAILED: 'Kon gegevens niet laden. Probeer opnieuw.',
  SAVE_FAILED: 'Kon wijzigingen niet opslaan. Probeer opnieuw.',

  // Payment errors
  PAYMENT_FAILED: 'Betaling mislukt. Probeer opnieuw.',
  PAYMENT_EXPIRED: 'Betaallink is verlopen. Maak een nieuwe aan.',

  // Route errors
  ROUTE_NOT_FOUND: 'Route niet gevonden.',
  STOP_NOT_FOUND: 'Stop niet gevonden.',

  // Item errors
  ITEM_LOAD_FAILED: 'Kon items niet laden.',
  ITEM_SAVE_FAILED: 'Kon item niet opslaan.',

  // Generic
  UNKNOWN_ERROR: 'Er is iets misgegaan. Probeer opnieuw.',
} as const;

// =============================================================================
// SUCCESS MESSAGES (Dutch)
// =============================================================================

export const SUCCESS_MESSAGES = {
  PAYMENT_REGISTERED: 'Betaling geregistreerd',
  PAYMENT_RECEIVED: 'Betaling ontvangen',
  STOP_COMPLETED: 'Stop voltooid',
  STOP_SKIPPED: 'Stop overgeslagen',
  ITEM_SAVED: 'Item opgeslagen',
  ITEM_ADDED: 'Item toegevoegd',
  ROUTE_SYNCED: 'Route gesynchroniseerd',
} as const;

// =============================================================================
// STORAGE KEYS
// =============================================================================

export const STORAGE_KEYS = {
  AUTH_STATE: 'kleda-auth-state',
  ROUTE_STATE: 'kleda-route-state',
  SYNC_QUEUE: 'kleda-sync-queue',
  LAST_SYNC: 'kleda-last-sync',
} as const;

// =============================================================================
// VOORINSPECTIE OPTIONS (Multi-picklist values)
// =============================================================================

/**
 * Groepen voor de Werkbank-render (plan 2026-09-02). Volgorde binnen 'vaak' is
 * een meting, geen mening: aantal keer gekozen in 2026 op mirror_wasserij_item
 * (19.319 keuzes, gemeten 02-09). `value` is de klanttekst die naar SF en het
 * voorinspectierapport gaat en blijft byte-gelijk; alleen label, groep en
 * volgorde zijn UI. Het smoke-script bewaakt de value-hash.
 */
export type VoorinspectieGroep = 'vaak' | 'schade' | 'geur' | 'kleur';

export const VOORINSPECTIE_GROEPEN: ReadonlyArray<{ key: VoorinspectieGroep; label: string }> = [
  { key: 'vaak', label: 'Vaak' },
  { key: 'schade', label: 'Schade' },
  { key: 'geur', label: 'Geur' },
  { key: 'kleur', label: 'Kleur en garantie' },
];

export const VOORINSPECTIE_OPTIONS = [
  // vaak — op frequentie (2026): 292 · 5227 · 4345 · 2931 · 685 · 673 · 653 · 479 · 219 · 155 · 16
  { value: 'Geen bijzonderheden.', label: 'Geen bijzonderheden', groep: 'vaak' },
  { value: 'Slijtage zal zichtbaar blijven.', label: 'Slijtage door gebruik', groep: 'vaak' },
  { value: 'Enkele vlekken kunnen zichtbaar blijven. Waar nodig zullen wij deze extra behandelen om tot een zo goed mogelijk resultaat te kunnen komen.', label: 'Vlekken/verkleuringen', groep: 'vaak' },
  { value: 'Looppad zal zichtbaar blijven.', label: 'Looppad zichtbaar', groep: 'vaak' },
  { value: 'Veel zand in het tapijt, we gaan proberen zoveel mogelijk eruit te kloppen zonder het tapijt (verder) te beschadigen.', label: 'Veel zand', groep: 'vaak' },
  { value: 'Tapijt loopt niet recht', label: 'Loopt niet recht', groep: 'vaak' },
  { value: 'Kan meer gaan pluizen.', label: 'Kan meer pluizen', groep: 'vaak' },
  { value: 'Diep vuil kan niet verwijderd worden', label: 'Diep vuil', groep: 'vaak' },
  { value: 'Tapijt hobbelt.', label: 'Tapijt hobbelt', groep: 'vaak' },
  { value: 'Lichte slijtage door gebruik.', label: 'Lichte slijtage', groep: 'vaak' },
  { value: 'Sterk vervuild', label: 'Sterk vervuild', groep: 'vaak' },
  // schade — 397 · 287 · 204 · 157 · 154 · 114 · 94 · 73 · 67 · 44 · 37
  { value: 'Slijtage gefestonneerde randen.', label: 'Slijtage gefestonneerde randen', groep: 'schade' },
  { value: 'De randen aan de achterkant van het vloerkleed laat op verschillende plekken los. Dit kan door de reiniging erger worden. Wij kunnen deze opnieuw voor u lijmen.', label: 'Randen laten los', groep: 'schade' },
  { value: 'Slijtage franje', label: 'Slijtage franje', groep: 'schade' },
  { value: 'De huidige schade aan het tapijt kan toenemen', label: 'Schade kan toenemen', groep: 'schade' },
  { value: 'Franje verkleurd', label: 'Franje verkleurd', groep: 'schade' },
  { value: 'Lange zijde beschadigd', label: 'Lange zijde beschadigd', groep: 'schade' },
  { value: 'Korte zijde beschadigd', label: 'Korte zijde beschadigd', groep: 'schade' },
  { value: 'Franje beschadigd', label: 'Franje beschadigd', groep: 'schade' },
  { value: '(Oude) Motschade', label: 'Motschade', groep: 'schade' },
  { value: 'Gat(en)', label: 'Gat(en)', groep: 'schade' },
  { value: 'Zwakke rug', label: 'Zwakke rug', groep: 'schade' },
  // geur — 203 · 5 · 3
  { value: 'Sterke geur', label: 'Sterke geur', groep: 'geur' },
  { value: 'Getuft vloerkleed, latex backing is oorzaak poeder.', label: 'Getuft - latex poeder', groep: 'geur' },
  { value: 'Geur van de latex backing kan niet verwijderd worden.', label: 'Latex geur', groep: 'geur' },
  // kleur en garantie — 909 · 604 · 143 · 87 · 62
  { value: 'Pootafdrukken kunnen zichtbaar blijven', label: 'Pootafdrukken', groep: 'kleur' },
  { value: 'Gele verkleuringen blijven zichtbaar', label: 'Gele verkleuringen', groep: 'kleur' },
  { value: 'Geen garantie op vlekken en/of verkleuringen', label: 'Geen garantie vlekken', groep: 'kleur' },
  { value: 'Lichte verkleuringen zullen zichtbaar blijven', label: 'Lichte verkleuringen', groep: 'kleur' },
  { value: 'Kleuruitloop waargenomen, dit zal zichtbaar blijven.', label: 'Kleuruitloop', groep: 'kleur' },
] as const satisfies ReadonlyArray<{ value: string; label: string; groep: VoorinspectieGroep }>;

export type VoorinspectieOption = typeof VOORINSPECTIE_OPTIONS[number]['value'];

/**
 * Voorinspectie-opties die op schade wijzen en dus (waarschijnlijk) een
 * risico-melding verdienen. Vinkt de medewerker één van deze aan terwijl er
 * geen melding loopt, dan krijgt hij bij het afronden een blokkerende keuze:
 * eerst melden of bewust zonder melding afronden. Achtergrond: WI-332384
 * (10-08) rondde af met "Gat(en)" zonder melding — checklist en risico-flow
 * zijn twee losse paden op hetzelfde scherm, en alleen aanvinken voelt als
 * "gemeld". Bewust alleen de schade-klasse; standaard-disclaimers (slijtage,
 * looppad, vlekken) horen hier niet in, anders wordt de prompt ruis.
 */
export const RISICO_INDICATIE_VOORINSPECTIE: ReadonlySet<string> = new Set([
  'Gat(en)',
  '(Oude) Motschade',
  'Zwakke rug',
  'Franje beschadigd',
  'Lange zijde beschadigd',
  'Korte zijde beschadigd',
  'De huidige schade aan het tapijt kan toenemen',
  'Kleuruitloop waargenomen, dit zal zichtbaar blijven.',
]);

// =============================================================================
// SOORT OPTIONS (Tapijt soorten)
// =============================================================================

export const SOORT_OPTIONS = [
  { value: 'Perzisch tapijt', label: 'Perzisch tapijt' },
  { value: '- Gabbeh', label: '  └ Gabbeh' },
  { value: '- Nain', label: '  └ Nain' },
  { value: 'Berber tapijt', label: 'Berber tapijt' },
  { value: '- Marokkaanse Berber', label: '  └ Marokkaanse Berber' },
  { value: 'Oosters tapijt', label: 'Oosters tapijt' },
  { value: '- Ziegler tapijt', label: '  └ Ziegler tapijt' },
  { value: '- Nepal tapijt', label: '  └ Nepal tapijt' },
  { value: '- Chinees tapijt', label: '  └ Chinees tapijt' },
  { value: '- Afghaans tapijt', label: '  └ Afghaans tapijt' },
  { value: '- Pakistaans tapijt', label: '  └ Pakistaans tapijt' },
  { value: 'Kelim tapijt', label: 'Kelim tapijt' },
  { value: 'Synthetisch tapijt', label: 'Synthetisch tapijt' },
  { value: '- Synthetisch Shaggy tapijt', label: '  └ Synthetisch Shaggy' },
  { value: '- Synthetisch kortpolig tapijt', label: '  └ Synthetisch kortpolig' },
  { value: 'Bolletjes tapijt', label: 'Bolletjes tapijt' },
  { value: 'Groene / Antislip rug', label: 'Groene / Antislip rug' },
  { value: 'Handgetuft tapijt', label: 'Handgetuft tapijt' },
  { value: 'Hoogpolig tapijt', label: 'Hoogpolig tapijt' },
  { value: 'India getuft tapijt', label: 'India getuft tapijt' },
  { value: 'Katoenen tapijt', label: 'Katoenen tapijt' },
  { value: 'Louis de Poortere tapijt', label: 'Louis de Poortere tapijt' },
  { value: 'Machinaal pers tapijt', label: 'Machinaal pers tapijt' },
  { value: 'Machinaal wol tapijt', label: 'Machinaal wol tapijt' },
  { value: 'Patchwork tapijt', label: 'Patchwork tapijt' },
  { value: 'Vintage tapijt', label: 'Vintage tapijt' },
  { value: 'Viscose tapijt', label: 'Viscose tapijt' },
  { value: 'Wol dik tapijt', label: 'Wol dik tapijt' },
  { value: 'Wol tapijt', label: 'Wol tapijt' },
  { value: 'Zijde tapijt', label: 'Zijde tapijt' },
  { value: 'Inloopmat', label: 'Inloopmat' },
  { value: 'Mat boot/caravan', label: 'Mat boot/caravan' },
  { value: 'Anders', label: 'Anders' },
] as const;

export type SoortOption = typeof SOORT_OPTIONS[number]['value'];

// =============================================================================
// MERK OPTIONS (Tapijt merken)
// =============================================================================

export const MERK_OPTIONS = [
  { value: '', label: 'Geen / Onbekend' },
  { value: 'Louis de Poortere', label: 'Louis de Poortere' },
  { value: 'Brink & Campman', label: 'Brink & Campman' },
  { value: 'Brinker', label: 'Brinker' },
  { value: 'De Munk Carpets', label: 'De Munk Carpets' },
  { value: 'Desso', label: 'Desso' },
  { value: 'Mart Visser', label: 'Mart Visser' },
  { value: 'Rugvista', label: 'Rugvista' },
  { value: 'Fraai', label: 'Fraai' },
  { value: 'Moooi Carpets', label: 'Moooi Carpets' },
  { value: 'HKliving', label: 'HKliving' },
] as const;

export type MerkOption = typeof MERK_OPTIONS[number]['value'];

// =============================================================================
// HOE REINIGEN OPTIONS (Cleaning methods)
// =============================================================================

/**
 * Volgorde is een meting (mirror_wasserij_item.hoe_reinigen, 2026, 02-09):
 * 2.922 · 2.649 · 1.188 · 578 · 17 · 3. `value` blijft byte-gelijk
 * (hash-bewaakt in scripts/smoke-ui-werkbank.mjs).
 */
export const HOE_REINIGEN_OPTIONS = [
  { value: 'Bad + Wasvloer', label: 'Bad + Wasvloer' },
  { value: 'Wasvloer', label: 'Wasvloer' },
  { value: 'Wasautomaat', label: 'Wasautomaat' },
  { value: 'Sproei-Extractie', label: 'Sproei-Extractie' },
  { value: 'Anders', label: 'Anders' },
  { value: 'Bonnet', label: 'Bonnet' },
] as const;

export type HoeReinigenOption = typeof HOE_REINIGEN_OPTIONS[number]['value'];

// =============================================================================
// NA-INSPECTIE OPTIONS (Kwaliteitscontrole acties)
// =============================================================================

/**
 * Na-inspectie actie opties met kleurcodering
 * - value: interne waarde voor opslag
 * - label: weergavenaam in UI
 * - targetStatus: de Status__c waarde na opslaan
 * - color: Tailwind kleurklasse voor visuele indicator
 */
export const NA_INSPECTIE_ACTIES = [
  // Volgorde: inpakken is de uitkomst van vrijwel elke controle (snapshot
  // 02-09: 363 Ingepakt tegen 13 nabehandeling, 13 opnieuw reinigen, 3 randen,
  // 3 reparatie in de mirror; er is geen statushistorie, dus dit is een
  // benadering). `value` en `targetStatus` blijven byte-gelijk.
  { value: 'inpakken', label: 'Inpakken', targetStatus: 'Ingepakt', color: 'green' },
  { value: 'nabehandelen', label: 'Nabehandelen', targetStatus: 'In Nabehandeling', color: 'orange' },
  { value: 'nogmaals_reinigen', label: 'Nogmaals reinigen', targetStatus: 'Klaar voor Reinigen', color: 'blue' },
  { value: 'randen_plakken', label: 'Randen plakken', targetStatus: 'Randen Plakken', color: 'purple' },
  { value: 'naar_reparatie', label: 'Naar reparatie', targetStatus: 'In Reparatie', color: 'red' },
] as const;

export type NaInspectieActie = typeof NA_INSPECTIE_ACTIES[number]['value'];

/** Acties waarbij de na-inspectie het kleed afkeurt; die vragen een reden. */
export const NA_INSPECTIE_AFKEUR_ACTIES: ReadonlySet<string> = new Set(['nabehandelen', 'nogmaals_reinigen']);

/** Afkeurredenen (plan 15-09); 'anders' draagt een korte tekst. */
export const NA_INSPECTIE_AFKEUR_REDENEN = [
  { value: 'geur', label: 'Geur' },
  { value: 'vlekken', label: 'Vlekken' },
  { value: 'anders', label: 'Anders' },
] as const;
export type NaInspectieAfkeurReden = typeof NA_INSPECTIE_AFKEUR_REDENEN[number]['value'];
export const NA_INSPECTIE_REDEN_TEKST_MAX = 80;
export type NaInspectieTargetStatus = typeof NA_INSPECTIE_ACTIES[number]['targetStatus'];

/**
 * Opmerkingen voor aflevering - multipicklist opties
 * Deze worden aan de klant gecommuniceerd bij aflevering.
 *
 * Groep en volgorde zijn een meting (mirror_wasserij_item
 * .opmerkingen_voor_aflevering, 2026, 02-09): 1.067 · 414 · 390 · 327 · 222 ·
 * 59 · 52 · 34 · 8 · 3. `value` blijft byte-gelijk (hash-bewaakt).
 */
export type OpmerkingenAfleveringGroep = 'vaak' | 'overig';

export const OPMERKINGEN_AFLEVERING_GROEPEN: ReadonlyArray<{ key: OpmerkingenAfleveringGroep; label: string }> = [
  { key: 'vaak', label: 'Vaak' },
  { key: 'overig', label: 'Overig' },
];

export const OPMERKINGEN_AFLEVERING_OPTIES = [
  { value: 'Vlekken/Verkleuringen hebben extra behandeling gehad maar blijven zichtbaar.', label: 'Vlekken/verkleuringen behandeld, blijven zichtbaar', groep: 'vaak' },
  { value: 'Best haalbare resultaat.', label: 'Best haalbare resultaat', groep: 'vaak' },
  { value: 'Slijtage blijft zichtbaar.', label: 'Slijtage blijft zichtbaar', groep: 'vaak' },
  { value: 'Looppad heeft extra behandeling gehad maar blijft zichtbaar.', label: 'Looppad behandeld, blijft zichtbaar', groep: 'vaak' },
  { value: 'Lichte verkleuringen blijven zichtbaar.', label: 'Lichte verkleuringen zichtbaar', groep: 'vaak' },
  { value: 'Diep vuil kan niet verwijderd worden zonder de vezel te beschadigen.', label: 'Diep vuil niet verwijderbaar', groep: 'overig' },
  { value: 'Slijtage franje blijft zichtbaar.', label: 'Slijtage franje zichtbaar', groep: 'overig' },
  { value: 'Uw tapijt is meerdere malen (intensief) geklopt, beste resultaat zonder de vezel of het tapijt te beschadigen.', label: 'Intensief geklopt, beste resultaat', groep: 'overig' },
  { value: 'Geur is van kleed.', label: 'Geur is van kleed', groep: 'overig' },
  { value: 'Geur komt van latex rug.', label: 'Geur komt van latex rug', groep: 'overig' },
] as const satisfies ReadonlyArray<{ value: string; label: string; groep: OpmerkingenAfleveringGroep }>;

export type OpmerkingenAfleveringOptie = typeof OPMERKINGEN_AFLEVERING_OPTIES[number]['value'];

/**
 * Priority product codes voor sortering in lijst weergave
 * Items met deze ProductCodes worden als eerste getoond als badges
 */
export const PRIORITY_PRODUCT_CODES = [
  'WAS-PRIM-VLOERKLEED-SPOED',
  'WAS-PACK-VLOERKLEED-PREMIUM',
] as const;

// =============================================================================
// RISICO-FLOW (matches Wasserij_item__c.Risico_*__c picklists)
// =============================================================================

/** Multi-picklist values for Wasserij_item__c.Risico_Types__c */
export const RISICO_TYPE_OPTIONS = [
  'Zwakke plek',
  'Schade aanwezig',
  'Kleurverlies risico',
  'Krimp risico',
  'Vlek mogelijk niet weg',
  'Geur mogelijk niet volledig weg',
  'Anders',
] as const;

export type RisicoType = typeof RISICO_TYPE_OPTIONS[number];

/** Picklist values for Wasserij_item__c.Risico_Status__c */
export type RisicoStatus =
  | 'Geen'
  | 'Gemeld'
  /** Kantoor bevestigde het risico; de klant-mail moet nog weg (v2). */
  | 'Bevestigd'
  | 'Wacht_klant'
  | 'Akkoord'
  | 'Niet_Akkoord'
  | 'Specialist_Akkoord'
  | 'Alternatief_gekozen';
